import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Loader2,
  Upload,
  FileSpreadsheet,
  CheckCircle2,
  AlertTriangle,
  Database,
  Download,
  ListChecks,
} from "lucide-react";

const COLUMNS = [
  { header: "Student Name", maps: "Student full name (match key)", required: true },
  { header: "Level", maps: "Level — values: 1, 2R, 2M", required: true },
  { header: "Teacher", maps: 'Assigned teacher(s) — separate multiple with " & "', required: true },
  { header: "Age", maps: "Age (whole number)", required: false },
  { header: "Gender", maps: "Male / Female", required: false },
  { header: "Early/Late", maps: "Session — E or Early / L or Late", required: false },
  { header: "Total Fees", maps: "Total fees owed", required: false },
  { header: "Paid Fees", maps: "Amount paid to date", required: false },
  { header: "Photo Consent", maps: "Yes / No", required: false },
  { header: "Parent Name", maps: "Guardian name", required: false },
  { header: "Phone 1", maps: "Guardian phone", required: false },
  { header: "Phone 2", maps: "Guardian phone 2", required: false },
  { header: "Email 1", maps: "Guardian email", required: false },
  { header: "Email 2", maps: "Guardian email 2", required: false },
  { header: "Medical", maps: "Medical notes", required: false },
  { header: "Admin Notes", maps: "Internal admin notes", required: false },
  { header: "Payment Date", maps: "Date of most recent payment — recommended format YYYY-MM-DD (e.g. 2026-01-15)", required: false },
  { header: "April Pts", maps: "April points → admin adjustment (no duplicates)", required: false },
  { header: "May Pts", maps: "May points → admin adjustment (no duplicates)", required: false },
];

function Stat({ label, value, testid, accent }) {
  return (
    <div className="bg-muted/40 rounded-lg p-4" data-testid={testid}>
      <p className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">{label}</p>
      <p className={`mt-1 font-heading text-2xl font-medium ${accent ? "text-primary" : "text-foreground"}`}>{value}</p>
    </div>
  );
}

function actionColor(line) {
  if (line.startsWith("Created")) return "text-[hsl(var(--success))]";
  if (line.startsWith("Updated")) return "text-primary";
  if (line.startsWith("Skipped")) return "text-muted-foreground";
  return "text-foreground";
}

export default function ImportData() {
  const [file, setFile] = useState(null);
  const [running, setRunning] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [result, setResult] = useState(null);
  const [counts, setCounts] = useState(null);

  const loadCounts = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/import/counts");
      setCounts(data);
    } catch (e) {
      /* non-critical */
    }
  }, []);

  useEffect(() => {
    loadCounts();
  }, [loadCounts]);

  const downloadTemplate = async () => {
    setDownloading(true);
    try {
      const { data } = await api.get("/admin/import/template.csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "import-template.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setDownloading(false);
    }
  };

  const run = async () => {
    if (!file) {
      toast.error("Choose a CSV file first");
      return;
    }
    setRunning(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/import/students-csv", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      loadCounts();
      toast.success("Import complete");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="max-w-3xl" data-testid="import-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-foreground">
        Import Data
      </h1>
      <p className="mt-1 text-muted-foreground">
        Upload the registration CSV to create or update teachers, students, and historical points.
      </p>

      {counts && (
        <p className="mt-3 inline-flex items-center gap-2 text-sm text-muted-foreground" data-testid="import-counts">
          <Database className="h-4 w-4 text-primary" />
          Database currently has <span className="font-medium text-foreground">{counts.students} students</span> and{" "}
          <span className="font-medium text-foreground">{counts.teachers} teachers</span>.
        </p>
      )}

      {/* Format documentation */}
      <div className="mt-6 bg-card border border-border rounded-lg p-6" data-testid="import-format-docs">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-primary" />
            <h2 className="font-heading text-lg font-medium text-foreground">Expected CSV format</h2>
          </div>
          <Button
            variant="outline"
            onClick={downloadTemplate}
            disabled={downloading}
            className="h-10"
            data-testid="download-template-button"
          >
            {downloading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
            Download template CSV
          </Button>
        </div>
        <p className="mt-3 text-sm text-muted-foreground">
          The CSV must include the following column headers. Extra columns are ignored. Existing students
          and teachers are matched by exact name and will be updated with the new values. Column names are
          matched loosely (spaces, underscores and capitalisation don't matter).
        </p>

        <div
          className="mt-4 rounded-lg border-l-4 border-[hsl(var(--gold))] bg-[hsl(var(--gold))]/10 px-4 py-3"
          data-testid="import-none-callout"
        >
          <p className="text-sm font-semibold text-foreground">How blank cells and “NONE” work</p>
          <ul className="mt-1.5 space-y-1 text-sm text-muted-foreground">
            <li>• Leave a cell <span className="font-medium text-foreground">blank</span> to keep the existing value unchanged.</li>
            <li>• Type <span className="font-mono font-semibold text-foreground">NONE</span> to explicitly clear that field.</li>
            <li>• For the <span className="font-medium text-foreground">Teacher</span> column, <span className="font-mono font-semibold text-foreground">NONE</span> removes all teacher assignments.</li>
            <li className="text-xs pt-0.5">(Student Name and Level are required — <span className="font-mono">NONE</span> is not allowed there.)</li>
          </ul>
        </div>

        <div className="mt-4 overflow-x-auto border border-border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Column header</TableHead>
                <TableHead>Maps to</TableHead>
                <TableHead>Required</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {COLUMNS.map((c) => (
                <TableRow key={c.header} data-testid={`format-row-${c.header.replace(/\s+/g, "-").toLowerCase()}`}>
                  <TableCell className="font-medium whitespace-nowrap">{c.header}</TableCell>
                  <TableCell className="text-muted-foreground">{c.maps}</TableCell>
                  <TableCell>
                    {c.required ? (
                      <span className="text-destructive font-medium">Required</span>
                    ) : (
                      <span className="text-muted-foreground">Optional</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Upload */}
      <div className="mt-6 bg-card border border-border rounded-lg p-6">
        <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
          <CheckCircle2 className="h-5 w-5 mt-0.5 text-[hsl(var(--success))] shrink-0" />
          <span>
            Safe to run more than once — existing teachers and students (matched by name) are updated in
            place, and re-importing the same April/May points won't create duplicates.
          </span>
        </div>

        <div className="mt-5 space-y-2">
          <Label htmlFor="import-file">Registration CSV</Label>
          <Input
            id="import-file"
            type="file"
            accept=".csv,text/csv"
            data-testid="import-file-input"
            className="h-11"
            onChange={(e) => {
              setFile(e.target.files?.[0] || null);
              setResult(null);
            }}
          />
          {file && <p className="text-xs text-muted-foreground" data-testid="import-file-name">Selected: {file.name}</p>}
        </div>

        <Button onClick={run} disabled={running || !file} className="mt-5 h-11" data-testid="run-import-button">
          {running ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
          Run Import
        </Button>
      </div>

      {result && (
        <div className="mt-6 bg-card border border-border rounded-lg p-6" data-testid="import-result">
          <div className="flex items-center gap-2 text-foreground">
            <CheckCircle2 className="h-5 w-5 text-[hsl(var(--success))]" />
            <h2 className="font-heading text-lg font-medium">Import summary</h2>
          </div>

          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Teachers created" value={result.teachers_created} testid="result-teachers-created" />
            <Stat label="Teachers updated" value={result.teachers_updated} testid="result-teachers-updated" />
            <Stat label="Teachers skipped" value={result.teachers_skipped} testid="result-teachers-skipped" />
            <Stat label="Adjustments created" value={result.adjustments_created} testid="result-adjustments-created" accent />
            <Stat label="Students created" value={result.students_created} testid="result-students-created" />
            <Stat label="Students updated" value={result.students_updated} testid="result-students-updated" />
            <Stat label="Students skipped" value={result.students_skipped} testid="result-students-skipped" />
            <Stat label="Notes / errors" value={result.errors?.length ?? 0} testid="result-errors-count" />
          </div>

          {result.actions_log?.length > 0 && (
            <div className="mt-5">
              <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                <ListChecks className="h-4 w-4 text-primary" />
                Actions ({result.actions_log.length})
              </div>
              <div className="mt-2 max-h-72 overflow-y-auto rounded-lg border border-border divide-y divide-border" data-testid="import-actions-list">
                {result.actions_log.map((line, i) => (
                  <div key={i} className={`px-4 py-2 text-sm ${actionColor(line)}`} data-testid={`import-action-${i}`}>
                    {line}
                  </div>
                ))}
              </div>
            </div>
          )}

          {result.errors?.length > 0 && (
            <div className="mt-5">
              <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                <AlertTriangle className="h-4 w-4 text-[hsl(var(--gold))]" />
                Row notes ({result.errors.length})
              </div>
              <div className="mt-2 max-h-64 overflow-y-auto rounded-lg border border-border divide-y divide-border" data-testid="import-errors-list">
                {result.errors.map((e, i) => (
                  <div key={i} className="px-4 py-2 text-sm" data-testid={`import-error-${i}`}>
                    <span className="font-medium text-foreground">{e.student}</span>
                    <span className="text-muted-foreground"> — {e.reason}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
